// apps/web/src/pages/account/RedeemInvite.tsx
//
// Plan 04-12b Task 3 — redeem invitation token.
//
// FLOW (CONTEXT D-09 post-signup redemption path + D-10 MFA gate):
//   1. User enters invitation token → POST /api/auth/invitations/redeem
//   2. On { ok: true } → "You can now author" + redirect to /
//   3. On { ok: false, reason: 'MFA_REQUIRED' } → "Set up MFA first" CTA
//      → /auth/mfa-enroll. The invitation is preserved (Plan 04-11 keeps
//      redeemed_at=NULL on the MFA-not-enrolled rejection) so the user can
//      retry with the same token after MFA is enrolled.
//   4. On { ok: false, reason: 'INVITATION_EXPIRED' | 'INVITATION_ALREADY_
//      REDEEMED' | 'INVITATION_NOT_FOUND' | 'SELF_REDEEM_FORBIDDEN' } →
//      show specific error.
//
// PRE-FILL FROM SESSIONSTORAGE:
//   SignUp stashes the invitation token under a sessionStorage key
//   `pending-invitation:<email>` for the post-verification redemption flow.
//   We DON'T auto-redeem (that would silently consume the token); we just
//   pre-fill the input so the user can confirm.

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { api } from "@/lib/api-client";
import { authClient, useSession } from "@/lib/auth-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const redeemSchema = z.object({
  token: z.string().min(1, "Enter your invitation token"),
});

type RedeemValues = z.infer<typeof redeemSchema>;

type RedeemReason =
  | "MFA_REQUIRED"
  | "INVITATION_NOT_FOUND"
  | "INVITATION_EXPIRED"
  | "INVITATION_ALREADY_REDEEMED"
  | "SELF_REDEEM_FORBIDDEN"
  | "EMAIL_NOT_VERIFIED";

interface RedeemResponse {
  ok: boolean;
  reason?: RedeemReason;
}

export function RedeemInvite(): React.ReactElement {
  const navigate = useNavigate();
  const session = useSession() as {
    data: { user?: { email?: string } } | null;
    isPending: boolean;
  };
  // Subscribed for completeness — used by sessionStorage pre-fill (no auth
  // call without it).
  void authClient;

  const [mfaRequired, setMfaRequired] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<RedeemValues>({
    resolver: zodResolver(redeemSchema),
    defaultValues: { token: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    // Pre-fill from sessionStorage if SignUp stashed a token for this email.
    const email = session.data?.user?.email;
    if (email === undefined || email === "") return;
    if (typeof window === "undefined" || !window.sessionStorage) return;
    try {
      const stashed = window.sessionStorage.getItem(
        `pending-invitation:${email.toLowerCase()}`,
      );
      if (stashed !== null && stashed.length > 0) {
        form.setValue("token", stashed);
      }
    } catch {
      // Pre-fill is a nicety; failure is silent.
    }
  }, [session.data?.user?.email, form]);

  async function onSubmit(values: RedeemValues): Promise<void> {
    setErrorMessage(null);
    setMfaRequired(false);
    setSuccess(false);
    try {
      const result = await api<RedeemResponse>(
        "/api/auth/invitations/redeem",
        {
          method: "POST",
          body: JSON.stringify({ token: values.token }),
        },
      );
      if (result.ok === true) {
        setSuccess(true);
        // Clear the stashed token now that it has been consumed.
        const email = session.data?.user?.email;
        if (
          email !== undefined &&
          typeof window !== "undefined" &&
          window.sessionStorage
        ) {
          try {
            window.sessionStorage.removeItem(
              `pending-invitation:${email.toLowerCase()}`,
            );
          } catch {
            // best-effort
          }
        }
        // Defer the redirect so the user sees the success state briefly.
        setTimeout(() => {
          navigate("/");
        }, 500);
        return;
      }
      const reason = result.reason;
      switch (reason) {
        case "MFA_REQUIRED":
          setMfaRequired(true);
          break;
        case "INVITATION_EXPIRED":
          setErrorMessage("This invitation has expired. Ask the issuer for a new one.");
          break;
        case "INVITATION_ALREADY_REDEEMED":
          setErrorMessage(
            "This invitation has already been used. Ask the issuer for a new one.",
          );
          break;
        case "INVITATION_NOT_FOUND":
          setErrorMessage("That invitation token is not valid.");
          break;
        case "SELF_REDEEM_FORBIDDEN":
          setErrorMessage("You cannot redeem an invitation you issued.");
          break;
        case "EMAIL_NOT_VERIFIED":
          setErrorMessage("Please verify your email address first.");
          break;
        default:
          setErrorMessage("Could not redeem this invitation.");
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Could not redeem this invitation",
      );
    }
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Redeem invitation</CardTitle>
          <CardDescription>
            Enter your invitation token to gain authoring access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {success ? (
            <Alert>
              <AlertTitle>Invitation redeemed</AlertTitle>
              <AlertDescription>
                You can now author recipes. Taking you home…
              </AlertDescription>
            </Alert>
          ) : mfaRequired ? (
            <>
              <Alert>
                <AlertTitle>Set up MFA first</AlertTitle>
                <AlertDescription>
                  This invitation requires two-factor authentication on your
                  account. Set up MFA, then come back to redeem with the same
                  token.
                </AlertDescription>
              </Alert>
              <Link
                to="/auth/mfa-enroll"
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Set up MFA
              </Link>
            </>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                {errorMessage !== null ? (
                  <Alert variant="destructive">
                    <AlertTitle>Redemption failed</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                ) : null}
                <FormField
                  control={form.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invitation token</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Paste your invitation token"
                          autoFocus
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Invitations are single-use and expire after 7 days.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? "Redeeming…" : "Redeem invitation"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/account/security" className="font-medium underline">
              Back to security
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
